<template>
  <div
    :class="[
      $style.root,
      { [$style.embedded]: footerPlacement === 'inline' },
      { [$style.embeddedRoot]: embeddedPageLayout },
    ]"
  >
    <form
      ref="topListingForm"
      novalidate
      :class="embeddedPageLayout ? $style.toplistingFormPage : $style.toplistingForm"
      @submit.prevent="submitForm"
    >
      <div :class="embeddedPageLayout ? $style.inputFields : $style.innerWrapper">
        <template v-if="showCurrentRegionalHighlightBooking">
          <h3>{{ t('toplisting.modal.yourCurrentBooking') }}</h3>
          <div class="row">
            <div :class="$style.notificationMessageContainer">
              <HgNotificationMessage theme="info">
                <template #icon>
                  <div :class="$style.currentBookingIconContainer">
                    <TopListingIcon :class="$style.icon" />
                  </div>
                </template>
                <div :class="$style.currentBookingMsgContainer">
                  <div :class="$style.currentBookingMsgBody">
                    {{
                      t('toplisting.modal.regionalHighlight.currentBooking', {
                        date: currentRegionalHighlightEndDate,
                      })
                    }}
                  </div>
                </div>
              </HgNotificationMessage>
            </div>
          </div>
        </template>
        <template v-if="showCurrentBookingNotification">
          <h3 v-if="!showCurrentRegionalHighlightBooking">
            {{ t('toplisting.modal.yourCurrentBooking') }}
          </h3>
          <div
            class="row"
            :class="{ [$style.currentBookingWithoutTitle]: showCurrentRegionalHighlightBooking }"
          >
            <div
              :class="[
                $style.notificationMessageContainer,
                {
                  [$style.notificationMessageLifetime]: isCurrentBookingLifetime,
                },
              ]"
            >
              <HgNotificationMessage theme="info">
                <template #icon>
                  <div :class="$style.currentBookingIconContainer">
                    <TopListingIcon :class="$style.icon" />
                  </div>
                </template>
                <div :class="$style.currentBookingMsgContainer">
                  <div :class="$style.currentBookingMsgTitle">
                    {{ currentBookingListingTypeTitle }}{{ currentBookingTitleSuffix }}
                  </div>
                  <div :class="$style.currentBookingMsgBody">
                    <template v-if="isCurrentBookingLifetime">
                      <span v-html="currentBookingLifetimeMessage"></span>
                    </template>
                    <template v-else>
                      <span>{{ currentBookingFormattedPeriodStartDate }}</span>
                      -
                      <span v-if="currentBookingFormattedPeriodEndDate.length">
                        <span>{{ currentBookingFormattedPeriodEndDate }}</span>
                        {{
                          t(`toplisting.modal.bookingLengthInDays`, {
                            days: currentBookingLengthInDays,
                          })
                        }}
                      </span>
                      <span v-else>
                        {{
                          t('toplisting.modal.endDate.noEndDate', {
                            listingType: currentBookingListingTypeTitle,
                          })
                        }}
                      </span>
                    </template>
                  </div>
                </div>
              </HgNotificationMessage>
            </div>
          </div>
        </template>

        <div v-if="isRegionalHighlightLoading" :class="$style.regionalHighlightLoading">
          <HgSpinner size="large" />
        </div>
        <ToplistingPromotionStep
          v-else-if="!isModalReadOnly"
          :modelValue="promotionRadioValue"
          :cards="promotionCardsForModal"
          :readonlyCards="promotionCardsLifetimeReadonly"
          :isModalReadOnly="isModalReadOnly"
          :useImprovedBenefitLines="true"
          :hidePromotionTypeHeader="hideModalPromotionIntroAndRecommendedBadge"
          :hideRecommendedBadge="
            hideModalPromotionIntroAndRecommendedBadge || isRegionalHighlightVisible
          "
          :radioName="promotionRadioName"
          @update:modelValue="onPromotionRadioSelect"
        >
          <template #afterPromotionDescription>
            <div
              v-if="embeddedPageLayout && !isModalReadOnly"
              :class="$style.noPromotionBeforePromotionCards"
            >
              <ToplistingNoPromotionChoice
                :modelValue="promotionRadioValue"
                :radioName="promotionRadioName"
                @update:modelValue="onPromotionRadioSelect"
              />
            </div>
          </template>
          <template #pricing-TOP_ABO>
            {{
              t('toplisting.modal.options.TOP_ABO.pricing', {
                numberOfListingAvailable: `${
                  topListingAbos!.totalSlots - topListingAbos!.activeSlots
                }/${topListingAbos!.totalSlots}`,
              })
            }}
          </template>
          <template #pricing-REGIONAL_HIGHLIGHT>
            {{
              t(
                hasActiveRegionalHighlight
                  ? 'toplisting.modal.regionalHighlight.price'
                  : 'toplisting.modal.regionalHighlight.slotAvailable',
                {
                  district: regionalHighlightDistrictLabel,
                },
              )
            }}
          </template>
          <template #pricing-TOP>
            <div :class="$style.priceContainer">
              <div :class="$style.priceWrapper">
                <span :class="$style.price"> {{ formatPromotionCardChf(toplistingPrice) }}</span>
                {{ t('toplisting.modal.perDay') }}
              </div>
            </div>
          </template>
          <template #pricing-PREMIUM>
            <div :class="$style.priceContainer">
              <div :class="$style.priceWrapper">
                <span :class="$style.price">{{
                  formatPromotionCardChf(toplistingPrices?.pricePremiumListingChf)
                }}</span>
                {{ t('toplisting.modal.perDay') }}
              </div>
            </div>
          </template>
          <template #pricing-LIFETIME>
            <div :class="$style.priceContainer">
              <div :class="$style.priceWrapper">
                <span :class="$style.price">{{
                  formatPromotionCardChf(lifetimeToplistingPrice)
                }}</span>
                {{ t('toplisting.modal.lifetimeOneTime') }}
              </div>
            </div>
          </template>
        </ToplistingPromotionStep>
        <HgNotificationMessage
          v-if="regionalHighlightWarning"
          :class="$style.regionalHighlightWarning"
          :title="regionalHighlightWarning.title"
          theme="warning"
          :showCloseButton="false"
        >
          <template #icon><HgIcon icon="Warning" /></template>
          {{ regionalHighlightWarning.body }}
        </HgNotificationMessage>
        <HgNotificationMessage
          v-if="regionalHighlightErrorMessage"
          :class="$style.regionalHighlightWarning"
          theme="danger"
          :showCloseButton="false"
        >
          {{ regionalHighlightErrorMessage }}
        </HgNotificationMessage>
        <div v-if="isModalReadOnly" :class="$style.disclaimerLifetime">
          <p>{{ t('toplisting.modal.disclaimerLifetime') }}</p>
        </div>
        <!-- TODO: Verify if this hint is still needed -->
        <!-- <p :class="$style.promotionHint">{{ t('toplisting.modal.promotionHint') }}</p> -->

        <template v-if="showBookingDurationAndHints">
          <h4>{{ t('toplisting.modal.durationImprovement') }}</h4>
          <div class="row">
            <div class="col-sm-6" :class="$style.startDateWrapper">
              <div @click="openDatePicker('start', $event)">
                <HgInput
                  v-model="startDate"
                  type="date"
                  name="beginRequest"
                  :label="t('toplisting.modal.startDate.name')"
                  pattern="\d{4}-\d{2}-\d{2}"
                  :min="minStartDate"
                  :max="endDate"
                  :isDirty="v$.startDate.$dirty"
                  :disabled="!areDateInputsEnabled || isStartDateDisabled"
                  :isInvalid="v$.startDate.$invalid"
                  @update:modelValue="onChangedDate"
                >
                  <template v-if="v$.startDate.$error" #errorMessages>
                    <span v-if="!!v$.startDate.$model && !v$.startDate.notInPast">
                      {{ t('toplisting.modal.startDate.notInPast') }}
                    </span>
                    <span v-if="!v$.startDate.$model">
                      {{
                        t('validation.requiredField', {
                          fieldName: t('toplisting.modal.startDate.name'),
                        })
                      }}
                    </span>
                  </template>
                </HgInput>
              </div>
            </div>
            <div class="col-sm-6" :class="$style.endDateField">
              <div @click.self="openDatePicker('end', $event)">
                <HgInput
                  v-model="computedEndDate"
                  type="date"
                  name="endRequest"
                  :label="t('toplisting.modal.endDate.name')"
                  pattern="\d{4}-\d{2}-\d{2}"
                  :min="minEndDate"
                  :isDirty="v$.endDate.$dirty"
                  :disabled="!areDateInputsEnabled"
                  :isInvalid="v$.endDate.$invalid"
                  @update:modelValue="onChangedDate"
                >
                  <template v-if="!isTLAboSelected" #tooltip>
                    <HgTooltip mode="hybrid" placement="top" theme="secondary">
                      <p :class="$style.durationHintTooltipBody">
                        {{ t('toplisting.modal.durationHint') }}
                      </p>
                    </HgTooltip>
                  </template>
                  <template v-if="v$.endDate.$error" #errorMessages>
                    <span v-if="!!v$.endDate.$model && !v$.endDate.notInPast">
                      {{ t('toplisting.modal.endDate.notInPast') }}
                    </span>
                    <span v-if="!!v$.endDate.$model && !v$.endDate.laterThanStartDate">
                      {{ t('toplisting.modal.endDate.laterThanStartDate') }}
                    </span>
                  </template>
                </HgInput>
              </div>
            </div>
          </div>
        </template>

        <div v-if="!isModalReadOnly && !isNoPromotionSelected" :class="$style.durationHint">
          <ToplistingCostSummary
            v-if="shouldShowCostSummary"
            :pricePerDay="selectedVariantPrice"
            :startDate="startDate"
            :endDate="endDate"
            :toplistingVariant="toplistingVariant"
            :isVisible="!isNoPromotionSelected"
            :toplistingTypeLabel="toplistingTypeLabel"
            :hasFlexAccess="hasFlexAccess"
            :offerType="insertionOfferType"
            :insertionStatus="insertion.status"
            :isNoPromotionSelected="isNoPromotionSelected"
            :isTLAboContractActive="isTLAboContractActive"
            :isRegionalHighlight="isRegionalHighlightSelected"
            :regionalHighlightDistrictLabel="regionalHighlightDistrictLabel"
          />
        </div>

        <div v-if="showImmoCreditsSimpleNotice" :class="$style.immoCreditsSimpleNotice">
          <HgNotificationMessage
            :title="t('listingPromotionPage.immoCreditsFunnelNotice.title')"
            :showCloseButton="false"
            theme="info"
          >
            <template #icon>
              <HgIcon icon="Info" />
            </template>
            <span>{{ t(immoCreditsSimpleNoticeBodyTranslationKey) }}</span>
            <span v-if="showImmoCreditsPackageOverviewLink">
              {{ t('listingPromotionPage.immoCreditsFunnelNotice.balanceLabel') }}
              <span v-if="immoCreditsSimpleNoticeBalance">
                {{ immoCreditsSimpleNoticeBalance }}
              </span>
            </span>
            <HgLink
              v-if="showImmoCreditsPackageOverviewLink"
              :class="$style.immoCreditsSimpleNoticeLink"
              :href="packageOverviewHref"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ t('listingPromotionPage.immoCreditsFunnelNotice.link') }}
            </HgLink>
          </HgNotificationMessage>
        </div>
      </div>
    </form>
    <!--    todo: move footer logic to ToplistingModal -->
    <div v-if="footerPlacement === 'inline' && active" :class="$style.inlineFooter">
      <HgLink
        v-if="hasActivePromotion && !isModalReadOnly"
        :class="$style.wrapperIcon"
        :disabled="isDeactivateButtonDisabled"
        @click="$emit('deactivate-request')"
      >
        <HgIcon icon="Delete" size="large" theme="secondary" />
        {{ deactivationLinkText }}
      </HgLink>
      <div :class="$style.inlineFooterButtons">
        <HgButton type="button" theme="primary-bordered" :size="ButtonSize.Small" @click="cancel">
          {{ t(`general.buttons.${!isModalReadOnly ? 'cancel' : 'close'}`) }}
        </HgButton>
        <HgButton
          v-if="!isSubmitButtonHidden && !isModalReadOnly"
          theme="primary"
          :size="ButtonSize.Small"
          :disabled="isPrimaryActionDisabled"
          @click="submitForm"
        >
          <LoadingIndicator v-if="submitLoadingIndicator" :class="$style.loadingIndicator" />
          {{ submitButtonText }}
        </HgButton>
      </div>
    </div>
    <div v-if="showImmoCreditsLoadingSkeleton" :class="$style.immoCreditsSkeletonBlock">
      <HgSkeleton :class="$style.skeletonHeader" />
      <HgSkeleton :class="$style.skeletonCollapsible" />
      <HgSkeleton :class="$style.skeletonParagraph" width="100%" />
      <HgSkeleton :class="$style.skeletonParagraph" width="70%" />
      <HgSkeleton :class="$style.skeletonParagraph" width="30%" />
    </div>

    <ToplistingCredit
      v-if="showToplistingCreditSection"
      :data="toplistingCreditUsage as any"
      :toplistingEndDate="endDate as any"
    />
  </div>
</template>

<script setup lang="ts">
/* eslint-disable max-lines */
import {
  ContractModel,
  fetchLifetimeToplistingCreditUsage,
  fetchToplistingCreditUsage,
  type LifetimeToplistingCreditUsage,
  type TopListingAbos,
  type ToplistingCreditUsage,
} from '@/api/toplistingService';
import LoadingIndicator from '@/assets/images/loading_indicator.svg?component';
import TopListingIcon from '@/assets/images/top-listing-icon.svg?component';
import ToplistingCostSummary from '@/components/ToplistingBookingForm/ToplistingCostSummary.vue';
import ToplistingNoPromotionChoice from '@/components/ToplistingPromotionCards/ToplistingNoPromotionChoice.vue';
import type { ToplistingPromotionCardConfig } from '@/components/ToplistingPromotionCards/types';
import ToplistingPromotionStep from '@/components/ToplistingPromotionStep/ToplistingPromotionStep.vue';
import {
  REGIONAL_HIGHLIGHT_PROMOTION_VALUE,
  TOPLISTING_PROMOTION_CARD_ACCENT_REGIONAL_HIGHLIGHT,
  TOPLISTING_PROMOTION_NONE,
} from '@/constants/toplistingPromotion';
import { formatListingAddress } from '@/helpers/Address/Address';
import {
  calculateDaysBetweenTwoDates,
  convertDateToDDMM,
  getCurrentDateInHgRetsFormat,
  isHgRetsDatePresentOrFuture,
  isUtcDateTimeInPast,
  mapToDotFormattedDate,
  mapUtcToDotFormattedDate,
  mapUtcToHgRetsDateFormat,
} from '@/helpers/dateUtils';
import { hasSmeAccess } from '@/helpers/hasSmeAccess';
import { formatNumberWithTwoDecimals, roundToTwoDecimals } from '@/helpers/numberUtils';
import { getRegionalHighlightBookingState } from '@/helpers/regionalHighlightBookingState';
import {
  buildToplistingLifetimeReadonlyStepCards,
  buildToplistingPromotionStepCards,
} from '@/helpers/toplistingPromotionStepCards';
import { ConversionSource, EventAction, EventLabel } from '@/services/tracking/GtmTrackingService';
import { trackToplistingBooking, trackToplistingInteraction } from '@/services/tracking/Tracking';
import { ApiCallStatus } from '@/store/insertions/InsertionsStore';
import {
  ToplistingModalAction,
  ToplistingSourceSystem,
  ToplistingVariant,
  type BookToplistingRequest,
  type Insertion,
  type ToplistingData,
  type ToplistingStatus,
} from '@/types';
import {
  RegionalHighlightCustomerRelation,
  RegionalHighlightListingMatchAction,
  RegionalHighlightSlotDisabledReason,
  type RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';
import {
  ButtonSize,
  HgButton,
  HgIcon,
  HgInput,
  HgLink,
  HgNotificationMessage,
  HgSkeleton,
  HgSpinner,
  HgTooltip,
} from '@smg-real-estate/domus';

import { getTopListingPrices_OLD } from '@/components/ToplistingBookingForm/replica-of-toplisting-prices-v2.1.0';
import ToplistingCredit from '@/components/ToplistingCredit/ToplistingCredit.vue';
import { getUbmPriceElasticityExperimentVariantArray } from '@/helpers/toplistingPriceElasticityTracking';
import { OfferType, RankType } from '@smg-real-estate/hg-rets-schema';
import {
  getLifetimeTopListingPrices,
  getTopListingPrices,
} from '@smg-real-estate/toplisting-prices';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const { t, locale } = useI18n();
const route = useRoute();

/** UBM currency style (ASR-1648) — same as cost summary / Immo-Credits amounts. */
function formatPromotionCardChf(value: number | string | undefined | null): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value !== 'number') {
    return value;
  }
  return `CHF ${formatNumberWithTwoDecimals(value, locale.value)}`;
}

const store = useStore();

const UBM_PRICE_ELASTICITY_EXPERIMENT = 'UBM_PRICE_ELASTICITY';
const UBM_PRICE_ELASTICITY_ROLLOUT_TIMESTAMP = Date.parse('2026-05-12T06:00:00.000Z');

const emit = defineEmits<{
  'update:title': [title: string];
  'update:footerBindings': [bindings: FooterBindings];
  'close-request': []; // eslint-disable-line sonarjs/no-duplicate-string
  'deactivate-request': [];
  /** Funnel promotion step: current promotion card / `NO_PROMOTION` when no upgrade. */
  'promotion-selection-change': [value: string];
}>();

export interface FooterBindings {
  isPrimaryActionDisabled: boolean;
  submitLoadingIndicator: boolean;
  submitButtonText: string;
  isSubmitButtonHidden: boolean;
  isModalReadOnly: boolean;
  toplistingModalAction: ToplistingModalAction;
  isDeactivateButtonDisabled: boolean;
  isNoPromotionSelected: boolean;
  deactivationKind?: 'toplisting' | 'regionalHighlight';
}

const props = defineProps({
  insertion: {
    type: Object as PropType<Insertion>,
    required: true,
  },
  /** When `inline`, render cancel/book (and optional deactivate) below the form (e.g. listing funnel). */
  footerPlacement: {
    type: String as PropType<'none' | 'inline'>,
    default: 'none',
  },
  /** Distinct radio `name` when multiple booking forms can exist in the DOM. */
  promotionRadioName: {
    type: String,
    default: 'toplistingVariant',
  },
  bookListingEventLabel: {
    type: String as PropType<EventLabel>,
    default: EventLabel.IN_MODAL,
  },
  /**
   * Funnel page layout: `form > .inputFields` (same as NewInsertionForm) and `display:contents` root
   * so the parent page's `.formWrapper` wraps the `<form>` directly in the box tree.
   */
  embeddedPageLayout: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: true,
  },
  conversionSource: {
    type: String as PropType<ConversionSource>,
    default: ConversionSource.BOOST_VISIBILITY,
  },
});

/** component data **/
const toplistingVariant = ref<ToplistingVariant>(ToplistingVariant.Top);
const promotionRadioValue = ref<string>(ToplistingVariant.Top);
const startDate = ref(getCurrentDateInHgRetsFormat());
const minStartDate = ref(getCurrentDateInHgRetsFormat());
const endDate = ref<string | null>(null);
const isStartDateDisabled = ref(false);
const toplistingStatus = ref<ToplistingStatus | null>(null);
const toplistingCreditUsage = ref<ToplistingCreditUsage | null>(null);
const lifetimeToplistingCreditUsage = ref<LifetimeToplistingCreditUsage | null>(null);
const showCreditErrorMsg = ref(false);
const creditDataCallCompleted = ref(false);
const formattedPeriodEndDate = ref('');
const currentBookingFormattedPeriodStartDate = ref('');
const currentBookingFormattedPeriodStartDateFull = ref('');
const currentBookingFormattedPeriodEndDate = ref('');
const currentBookingLengthInDays = ref<number | null>(null);
const initialFormValues = ref<ToplistingStatus | null>(null);
const currentBookingListingTypeTitle = ref('');
const topAboPreselectApplied = ref(false);

/**
 * Toplisting data for the current insertion, guarded by the store modal action.
 * Mirrors the old Options API `activeToplistingData` computed:
 * only returns data when the store says we're in EDIT mode.
 * This avoids treating an empty `{}` from the API as valid toplisting data.
 */
const hasToplistingData = (data: ToplistingData | undefined): data is ToplistingData =>
  !!data && Object.keys(data).length > 0;

const mapToplistingRequestDateToHgRets = (date: string | undefined): string | null => {
  if (!date) {
    return null;
  }
  const requestDate = new Date(date);
  if (Number.isNaN(requestDate.getTime())) {
    return null;
  }
  return mapUtcToHgRetsDateFormat(requestDate.toISOString());
};

const hasActiveEndRequest = (data: ToplistingData): boolean => {
  if (!data.endRequest) {
    return true;
  }
  const endRequest = new Date(data.endRequest);
  if (Number.isNaN(endRequest.getTime())) {
    return false;
  }
  return !isUtcDateTimeInPast(endRequest.toISOString());
};

const currentBookingToplistingData = computed<ToplistingData | undefined>(() => {
  if (props.embeddedPageLayout || !hasToplistingData(props.insertion.toplistingData)) {
    return undefined;
  }

  return hasActiveEndRequest(props.insertion.toplistingData)
    ? props.insertion.toplistingData
    : undefined;
});

const activeToplistingData = computed<ToplistingData | undefined>(() => {
  if (!hasToplistingData(props.insertion.toplistingData)) {
    return undefined;
  }
  return hasActiveEndRequest(props.insertion.toplistingData)
    ? props.insertion.toplistingData
    : undefined;
});

const hasActiveToplisting = computed<boolean>(() => Boolean(activeToplistingData.value));
const hasSavedDraftToplisting = computed<boolean>(() => {
  if (!props.embeddedPageLayout) {
    return false;
  }

  const valueAddedServices = props.insertion.listing?.valueAddedServices;
  return Boolean(valueAddedServices?.rank?.type || valueAddedServices?.isLifetimeToplisting);
});

const isToplistingApiCallInProgress = computed<boolean>(
  () => store.getters.isToplistingApiCallInProgress,
);
const topListingAbos = computed<TopListingAbos | undefined>(() => store.getters.getTopListingAbos);
const isImmoCreditsCustomer = computed<boolean>(
  () => store.getters.getIsImmoCreditsCustomer === true,
);
const packageOverviewHref = computed<string>(() => {
  const currentLocale = (route?.params?.locale as string | undefined) || locale.value;
  return `/${currentLocale}/ubm/agency/package-overview#immo-credits`;
});
const getActiveAgencyId = computed<string | undefined>(() => store.getters.getActiveAgencyId);
const getActiveProducts = computed(() => store.getters.getActiveProducts);

const getExistingBookingEndDate = (): string | null => {
  if (initialFormValues.value?.endRequest) {
    return initialFormValues.value.endRequest;
  }

  if (toplistingStatus.value?.endRequest) {
    return mapUtcToHgRetsDateFormat(toplistingStatus.value.endRequest);
  }

  return mapToplistingRequestDateToHgRets(activeToplistingData.value?.endRequest);
};

const shouldRestoreEndDateOnClear = (): boolean => {
  if (props.embeddedPageLayout) {
    return false;
  }

  if (!hasActiveToplisting.value) {
    return false;
  }

  if (!toplistingStatus.value || !activeToplistingData.value) {
    return false;
  }

  if (toplistingVariant.value === ToplistingVariant.TopAbo) {
    return false;
  }

  return getExistingBookingEndDate() !== null;
};

/** computed **/
const computedEndDate = computed({
  get(): string {
    return endDate.value !== null ? endDate.value : '';
  },
  set(value: string) {
    const trimmed = value.trim();

    if (!trimmed && shouldRestoreEndDateOnClear()) {
      const bookedEnd = getExistingBookingEndDate();
      if (bookedEnd) {
        endDate.value = bookedEnd;
        return;
      }
    }

    endDate.value = trimmed || null;
  },
});

const legacyToplistingPrices = computed(() => getTopListingPrices_OLD(getActiveAgencyId.value));

const toplistingPrices = computed(() => {
  if (isImmoCreditsCustomer.value) {
    return getTopListingPrices(getActiveAgencyId.value);
  }

  return getTopListingPrices(getActiveAgencyId.value, {
    experiment: UBM_PRICE_ELASTICITY_EXPERIMENT,
  });
});

const ubmPriceElasticityExperimentVariantArray = computed<string | undefined>(() => {
  return getUbmPriceElasticityExperimentVariantArray({
    customerId: getActiveAgencyId.value,
    objectReference: props.insertion.objectReference,
  });
});

const ubmPriceElasticityTrackingParams = computed<{
  experimentVariantArray?: string;
}>(() => {
  const experimentVariantArray = ubmPriceElasticityExperimentVariantArray.value;

  return experimentVariantArray !== undefined ? { experimentVariantArray } : {};
});

const lifetimeTopListingPrices = computed(() => getLifetimeTopListingPrices());

const insertionOfferType = computed(() => props.insertion.listing?.offerType);
const regionalHighlightListingId = computed<string>(() => props.insertion.listing?.id ?? '');
const regionalHighlightListingHasActiveStatus = computed<boolean>(() => {
  const statuses = store.getters.getRegionalHighlightListingStatuses as
    | Record<string, { hasRegionalHighlight?: boolean }>
    | undefined;
  return statuses?.[regionalHighlightListingId.value]?.hasRegionalHighlight === true;
});
const regionalHighlightEnabledForListing = computed<boolean>(
  () =>
    (insertionOfferType.value === OfferType.Buy || regionalHighlightListingHasActiveStatus.value) &&
    regionalHighlightListingId.value.length > 0,
);
const regionalHighlightSlotsResponse = computed<RegionalHighlightSlotsResponse | undefined>(
  () => store.getters.getRegionalHighlightSlotsResponse,
);
const regionalHighlightBookingState = computed(() =>
  getRegionalHighlightBookingState(
    regionalHighlightSlotsResponse.value,
    regionalHighlightListingId.value,
  ),
);
const isRegionalHighlightVisible = computed<boolean>(
  () =>
    regionalHighlightEnabledForListing.value &&
    ['assignable', 'blocked', 'active'].includes(regionalHighlightBookingState.value.kind),
);
const regionalHighlightSlot = computed(() =>
  'slot' in regionalHighlightBookingState.value
    ? regionalHighlightBookingState.value.slot
    : undefined,
);
const isRegionalHighlightSelected = computed<boolean>(
  () => promotionRadioValue.value === REGIONAL_HIGHLIGHT_PROMOTION_VALUE,
);
const hasActiveRegionalHighlight = computed<boolean>(
  () => regionalHighlightBookingState.value.kind === 'active',
);
const hasActivePromotion = computed<boolean>(
  () => hasActiveToplisting.value || hasActiveRegionalHighlight.value,
);
const regionalHighlightDistrictLabel = computed<string>(
  () => regionalHighlightSlot.value?.districtLabel || regionalHighlightSlot.value?.districtId || '',
);
const replacedRegionalHighlightListingLabel = computed<string>(() => {
  const listingId = regionalHighlightSlot.value?.activeBooking?.listingId;
  if (!listingId) {
    return '';
  }

  const insertions = store.getters.getInsertions as Insertion[] | undefined;
  const replacedInsertion = insertions?.find((insertion) => insertion.listing?.id === listingId);
  const street = replacedInsertion?.listing?.address?.street;

  return (
    (street?.trim() && replacedInsertion && formatListingAddress(replacedInsertion.listing)) ||
    replacedInsertion?.objectReference ||
    listingId
  );
});
const currentRegionalHighlightEndDate = computed<string>(() => {
  const contractEndDate = regionalHighlightSlot.value?.contractEndDate;
  return contractEndDate ? mapToDotFormattedDate(contractEndDate.slice(0, 10)) : '';
});
const showCurrentRegionalHighlightBooking = computed<boolean>(
  () => hasActiveRegionalHighlight.value && !props.embeddedPageLayout,
);
const regionalHighlightApiStatus = computed(() => store.getters.getRegionalHighlightApiStatus);
const isRegionalHighlightLoading = computed<boolean>(
  () =>
    regionalHighlightEnabledForListing.value &&
    regionalHighlightApiStatus.value?.slots === ApiCallStatus.IN_PROGRESS,
);
const regionalHighlightErrors = computed(() => store.getters.getRegionalHighlightErrors ?? {});
const isRegionalHighlightApiCallInProgress = computed<boolean>(() =>
  ['slots', 'listingStatuses', 'assign', 'deactivate'].some(
    (operation) => regionalHighlightApiStatus.value?.[operation] === ApiCallStatus.IN_PROGRESS,
  ),
);
const regionalHighlightErrorMessage = computed<string>(() => {
  if (
    !regionalHighlightEnabledForListing.value ||
    (!regionalHighlightErrors.value.slots &&
      !regionalHighlightErrors.value.listingStatuses &&
      !regionalHighlightErrors.value.assign &&
      !regionalHighlightErrors.value.deactivate)
  ) {
    return '';
  }
  return t('toplisting.modal.regionalHighlight.error');
});
const regionalHighlightWarning = computed<{
  title: string;
  body: string;
} | null>(() => {
  if (!isRegionalHighlightSelected.value || !regionalHighlightSlot.value) {
    return null;
  }

  if (
    regionalHighlightBookingState.value.kind === 'assignable' &&
    regionalHighlightSlot.value.listingMatch?.action === RegionalHighlightListingMatchAction.REPLACE
  ) {
    return {
      title: t('toplisting.modal.regionalHighlight.replaceWarning.title', {
        district: regionalHighlightDistrictLabel.value,
      }),
      body: t('toplisting.modal.regionalHighlight.replaceWarning.body', {
        listing: replacedRegionalHighlightListingLabel.value,
      }),
    };
  }

  if (regionalHighlightBookingState.value.kind !== 'blocked') {
    return null;
  }

  const isSiblingAssignment =
    regionalHighlightSlot.value.controls.disabledReason ===
      RegionalHighlightSlotDisabledReason.SIBLING_ASSIGNMENT ||
    regionalHighlightSlot.value.activeBooking?.customerRelation ===
      RegionalHighlightCustomerRelation.SAME_HEAD_SIBLING;
  const key = isSiblingAssignment ? 'siblingWarning' : 'unavailableWarning';
  return {
    title: t(`toplisting.modal.regionalHighlight.${key}.title`),
    body: t(`toplisting.modal.regionalHighlight.${key}.body`, {
      district: regionalHighlightDistrictLabel.value,
    }),
  };
});
const deactivationLinkText = computed<string>(() =>
  hasActiveRegionalHighlight.value
    ? t('toplisting.modal.regionalHighlight.deactivate.link')
    : t('toplisting.modal.deactivate'),
);

/**
 * Object-reference for GTM (`h_listing_object_ref_id_123`) only when listing id is absent,
 * same as ListingActions / promotion page (avoid dual identifiers).
 */
const toplistingTrackingObjectReferenceId = computed((): string | undefined =>
  props.insertion.listing?.id ? undefined : props.insertion.objectReference || undefined,
);

/** Modal edit of an existing promotion: hide intro + "recommended" badge (funnel page unchanged). */
const hideModalPromotionIntroAndRecommendedBadge = computed<boolean>(
  () => !props.embeddedPageLayout && hasActivePromotion.value,
);

const isNoPromotionSelected = computed<boolean>(
  () => promotionRadioValue.value === TOPLISTING_PROMOTION_NONE,
);

const showBookingDurationAndHints = computed<boolean>(() => {
  if (isModalReadOnly.value || isRegionalHighlightSelected.value) {
    return false;
  }
  return !isNoPromotionSelected.value;
});

const isPackageOverviewAccessible = computed<boolean>(
  () =>
    store.getters.getIsBronzePackage ||
    store.getters.getIsSilverPackage ||
    store.getters.getIsGoldPackage,
);

const showImmoCreditsPackageOverviewLink = computed<boolean>(
  () => isPackageOverviewAccessible.value,
);

/** Hide rich Immo-Credits UI when users should only get the fallback notice. */
const shouldHideImmoCreditsRichUi = computed<boolean>(() => {
  if (props.embeddedPageLayout) {
    return true;
  }

  return isPackageOverviewAccessible.value;
});

const isLifetimeToplistingSelected = computed<boolean>(
  () => !isNoPromotionSelected.value && toplistingVariant.value === ToplistingVariant.Lifetime,
);

const showImmoCreditsLoadingSkeleton = computed<boolean>(
  () =>
    isImmoCreditsCustomer.value &&
    !creditDataCallCompleted.value &&
    !isModalReadOnly.value &&
    !isNoPromotionSelected.value &&
    !isLifetimeToplistingSelected.value &&
    !shouldHideImmoCreditsRichUi.value &&
    !isRegionalHighlightSelected.value,
);

const showToplistingCreditSection = computed<boolean>(
  () =>
    creditDataCallCompleted.value &&
    !!(toplistingCreditUsage.value || showCreditErrorMsg.value) &&
    !isModalReadOnly.value &&
    !isNoPromotionSelected.value &&
    !isLifetimeToplistingSelected.value &&
    !shouldHideImmoCreditsRichUi.value &&
    !isRegionalHighlightSelected.value,
);

const showImmoCreditsSimpleNotice = computed<boolean>(
  () =>
    isImmoCreditsCustomer.value &&
    !isModalReadOnly.value &&
    !isNoPromotionSelected.value &&
    (shouldHideImmoCreditsRichUi.value || isLifetimeToplistingSelected.value) &&
    !isRegionalHighlightSelected.value,
);

const immoCreditsSimpleNoticeBodyTranslationKey = computed<string>(() => {
  if (promotionRadioValue.value === ToplistingVariant.Lifetime) {
    return 'listingPromotionPage.immoCreditsFunnelNotice.bodyLifetime';
  }
  return 'listingPromotionPage.immoCreditsFunnelNotice.body';
});
const immoCreditsSimpleNoticeBalance = computed<string | null>(() => {
  if (!showImmoCreditsPackageOverviewLink.value) {
    return null;
  }

  const availableCreditInCHFCents =
    toplistingVariant.value === ToplistingVariant.Lifetime
      ? lifetimeToplistingCreditUsage.value?.availableCreditInCHFCents
      : toplistingCreditUsage.value?.availableCreditInCHFCents;

  if (availableCreditInCHFCents === undefined || availableCreditInCHFCents === null) {
    return null;
  }

  return formatNumberWithTwoDecimals(availableCreditInCHFCents / 100, locale.value);
});

const hasFlexAccess = computed<boolean>(() => hasSmeAccess(getActiveProducts.value));

const shouldShowCostSummary = computed<boolean>(
  () =>
    isRegionalHighlightSelected.value ||
    toplistingVariant.value === ToplistingVariant.Lifetime ||
    toplistingVariant.value === ToplistingVariant.Top ||
    toplistingVariant.value === ToplistingVariant.Premium ||
    (toplistingVariant.value === ToplistingVariant.TopAbo && isTLAboContractActive.value) ||
    isNoPromotionSelected.value,
);

const toplistingTypeLabel = computed<string>(() => {
  if (toplistingVariant.value === ToplistingVariant.Premium) {
    return t('toplisting.badge.premium');
  } else if (toplistingVariant.value === ToplistingVariant.Top) {
    return t('toplisting.badge.top');
  } else if (toplistingVariant.value === ToplistingVariant.TopAbo) {
    return t('toplisting.badge.abo');
  } else if (toplistingVariant.value === ToplistingVariant.Lifetime) {
    return t('toplisting.badge.lifetime');
  }
  return '';
});

const isBeforeUbmPriceElasticityRollout = (date: string | number | undefined): boolean => {
  if (date === undefined) {
    return false;
  }

  const timestamp = new Date(date).getTime();
  return Number.isFinite(timestamp) && timestamp < UBM_PRICE_ELASTICITY_ROLLOUT_TIMESTAMP;
};

const legacyToplistingPrice = computed<number | string>(() => {
  if (insertionOfferType.value && legacyToplistingPrices.value) {
    return insertionOfferType.value === OfferType.Rent
      ? legacyToplistingPrices.value.priceTopListingRentChf
      : legacyToplistingPrices.value.priceTopListingBuyChf;
  }
  return '';
});

const shouldUsePreRolloutLegacyToplistingPrice = computed<boolean>(() => {
  const data = activeToplistingData.value;

  if (!data) {
    return false;
  }

  const isTransactionServiceElasticToplistingCandidate =
    // TODO: uncomment this when the insertion funnel passed the source system properly
    // props.insertion.listing?.meta?.source === Source.BusinessInsertionFunnel &&
    data.listingType === RankType.Top &&
    data.contractModel === ContractModel.DAILY &&
    data.sourceSystem !== ToplistingSourceSystem.RULE_AUTOMATIC;

  return (
    !isTransactionServiceElasticToplistingCandidate ||
    isBeforeUbmPriceElasticityRollout(data.beginRequest)
  );
});

const toplistingPrice = computed<number | string>(() => {
  if (shouldUsePreRolloutLegacyToplistingPrice.value) {
    return legacyToplistingPrice.value;
  }

  if (insertionOfferType.value && toplistingPrices.value) {
    return insertionOfferType.value === OfferType.Rent
      ? toplistingPrices.value.priceTopListingRentChf
      : toplistingPrices.value.priceTopListingBuyChf;
  }
  return '';
});

const lifetimeToplistingPrice = computed<number | string>(() => {
  if (insertionOfferType.value && lifetimeTopListingPrices.value) {
    return insertionOfferType.value === OfferType.Rent
      ? lifetimeTopListingPrices.value.priceLifetimeTopListingRentChf
      : lifetimeTopListingPrices.value.priceLifetimeTopListingBuyChf;
  }
  return '';
});

const selectedVariantPrice = computed<number | string>(() => {
  if (toplistingVariant.value === ToplistingVariant.Premium) {
    return toplistingPrices.value.pricePremiumListingChf;
  }
  if (toplistingVariant.value === ToplistingVariant.Lifetime && lifetimeTopListingPrices.value) {
    return insertionOfferType.value === OfferType.Rent
      ? lifetimeTopListingPrices.value.priceLifetimeTopListingRentChf
      : lifetimeTopListingPrices.value.priceLifetimeTopListingBuyChf;
  }
  // For Top variant, use toplistingPrice
  return toplistingPrice.value;
});

const shouldDisplayLifetimePricingSlot = computed<boolean>(
  () => lifetimeToplistingPrice.value !== '' && lifetimeToplistingPrice.value !== undefined,
);

const shouldDisplayPricingSlot = computed<boolean>(() => !!toplistingPrices.value);

const activeToplistingDays = computed<number>(() => {
  if (!activeToplistingData.value) {
    return 0;
  }

  const beginRequest = new Date(activeToplistingData.value.beginRequest);
  const now = new Date();

  const timeDifferenceInMillis = Math.abs(now.getTime() - beginRequest.getTime());
  const millisecondsInADay = 1000 * 60 * 60 * 24;

  return Math.ceil(timeDifferenceInMillis / millisecondsInADay);
});

const TLAbo30DaysFromBeginRequest = computed<string>(() => {
  const startDateAsDate = new Date(startDate.value);
  return mapUtcToDotFormattedDate(
    new Date(startDateAsDate.setDate(startDateAsDate.getDate() + 30)).toISOString(),
  );
});

const isTLAboSelected = computed<boolean>(() => {
  if (isNoPromotionSelected.value) {
    return false;
  }
  return toplistingVariant.value === ToplistingVariant.TopAbo;
});

const isTLAboContractActive = computed<boolean>(() => (topListingAbos.value?.totalSlots || 0) > 0);

const isActiveToplistingTLAbo = computed<boolean>(
  () => activeToplistingData.value?.contractModel === ContractModel.ABO,
);

const isTLAboActiveForLessThan30Days = computed<boolean>(
  () =>
    !!(
      activeToplistingData.value &&
      isActiveToplistingTLAbo.value &&
      activeToplistingDays.value < 30
    ),
);

const isTopAboRadioButtonEnabled = computed<boolean>(() => {
  if (!isTLAboContractActive.value || !topListingAbos.value) {
    return false;
  }

  if (topListingAbos.value!.activeSlots < topListingAbos.value!.totalSlots) {
    return true;
  }

  return !!(activeToplistingData.value && isActiveToplistingTLAbo.value);
});

const shouldPreselectTopAboInBoostVisibilityModal = computed<boolean>(
  () =>
    !props.embeddedPageLayout &&
    !hasActiveToplisting.value &&
    !activeToplistingData.value &&
    isTopAboRadioButtonEnabled.value,
);

const isEditingExistingAboBooking = computed<boolean>(
  () => hasActiveToplisting.value && isActiveToplistingTLAbo.value,
);

const isModalReadOnly = computed<boolean>(
  () => activeToplistingData.value?.contractModel === ContractModel.LIFETIME,
);

const isCurrentBookingLifetime = computed<boolean>(
  () => currentBookingToplistingData.value?.contractModel === ContractModel.LIFETIME,
);
const showCurrentBookingNotification = computed<boolean>(
  () => currentBookingFormattedPeriodStartDate.value.length > 0,
);
const currentBookingTitleSuffix = computed<string>(() =>
  isCurrentBookingLifetime.value ? '.' : ',',
);
const currentBookingLifetimeMessage = computed<string>(() =>
  t('toplisting.modal.currentBookingLifetime', {
    startDate: `<strong>${currentBookingFormattedPeriodStartDateFull.value}</strong>`,
    endDate: `<strong>${currentBookingFormattedPeriodEndDate.value}</strong>`,
  }),
);

const areDateInputsEnabled = computed<boolean>(
  () =>
    toplistingVariant.value !== ToplistingVariant.TopAbo &&
    toplistingVariant.value !== ToplistingVariant.Lifetime,
);

const isDeactivateButtonDisabled = computed<boolean>(
  () => !!(activeToplistingData.value && isTLAboActiveForLessThan30Days.value),
);

const isSubmitButtonHidden = computed<boolean>(
  () =>
    !!(
      activeToplistingData.value &&
      isTLAboActiveForLessThan30Days.value &&
      toplistingVariant.value === ToplistingVariant.TopAbo
    ),
);

const minEndDate = computed<string>(() => {
  const currentDate = getCurrentDateInHgRetsFormat();
  return startDate.value < currentDate ? currentDate : startDate.value;
});

const submitButtonText = computed<string>(() => {
  if (isRegionalHighlightSelected.value) {
    return t('toplisting.modal.regionalHighlight.visibilityUpgrade');
  }
  if (
    toplistingVariant.value === ToplistingVariant.Top ||
    toplistingVariant.value === ToplistingVariant.Premium
  ) {
    return t('toplisting.bookUpgrade');
  }
  if (!activeToplistingData.value && !hasActiveRegionalHighlight.value) {
    return t('toplisting.bookUpgrade');
  }
  return t('toplisting.adjustUpgrade');
});

const toplistingModalTitle = computed<string>(() => {
  if (!isModalReadOnly.value) {
    return t('toplisting.modal.promotionType.header');
  }
  if (hasActiveToplisting.value) {
    if (toplistingVariant.value === ToplistingVariant.Lifetime) {
      return t('toplisting.modal.title.lifetime');
    } else {
      const titleKey = toplistingVariant.value === ToplistingVariant.Top ? 'top' : 'premium';
      return t(`toplisting.modal.title.edit.${titleKey}`);
    }
  } else {
    return t(`toplisting.modal.title.create`);
  }
});

/** vuelidate **/
const validationRules = computed(() => ({
  startDate: {
    required,
    notInPast: (value: string): boolean => {
      if (isStartDateDisabled.value) {
        return true;
      }
      return isHgRetsDatePresentOrFuture(value);
    },
  },
  endDate: {
    notInPast: (value: string): boolean => {
      if (!value) return true;
      return isHgRetsDatePresentOrFuture(value);
    },
    laterThanStartDate: (value: string): boolean => {
      return value ? value >= startDate.value : true;
    },
  },
  toplistingVariant: {},
}));

const shouldWaitForImmoCreditsData = computed<boolean>(
  () =>
    isImmoCreditsCustomer.value &&
    !shouldHideImmoCreditsRichUi.value &&
    !isLifetimeToplistingSelected.value &&
    !isNoPromotionSelected.value &&
    !isRegionalHighlightSelected.value,
);

const isPrimaryActionDisabled = computed<boolean>(() => {
  if (isRegionalHighlightSelected.value) {
    return (
      regionalHighlightBookingState.value.kind === 'blocked' ||
      regionalHighlightBookingState.value.kind === 'active' ||
      isRegionalHighlightApiCallInProgress.value
    );
  }
  return (
    isNoPromotionSelected.value ||
    isToplistingApiCallInProgress.value ||
    (!v$.value.$dirty && !!activeToplistingData.value) ||
    (shouldWaitForImmoCreditsData.value && !creditDataCallCompleted.value)
  );
});

const submitLoadingIndicator = computed<boolean>(
  () =>
    isToplistingApiCallInProgress.value ||
    isRegionalHighlightApiCallInProgress.value ||
    (shouldWaitForImmoCreditsData.value && !creditDataCallCompleted.value),
);

const footerBindings = computed<FooterBindings>(() => ({
  isPrimaryActionDisabled: isPrimaryActionDisabled.value,
  submitLoadingIndicator: submitLoadingIndicator.value,
  submitButtonText: submitButtonText.value,
  isSubmitButtonHidden: isSubmitButtonHidden.value,
  isModalReadOnly: isModalReadOnly.value,
  toplistingModalAction: hasActivePromotion.value
    ? ToplistingModalAction.EDIT
    : ToplistingModalAction.CREATE,
  isDeactivateButtonDisabled: isDeactivateButtonDisabled.value,
  isNoPromotionSelected: isNoPromotionSelected.value,
  deactivationKind: hasActiveRegionalHighlight.value ? 'regionalHighlight' : 'toplisting',
}));

const promotionCardsForModal = computed<ToplistingPromotionCardConfig[]>(() => {
  const cards = buildToplistingPromotionStepCards({
    isTLAboContractActive: isTLAboContractActive.value,
    isTopAboRadioButtonEnabled: isTopAboRadioButtonEnabled.value,
    shouldDisplayPricingSlot: shouldDisplayPricingSlot.value,
    shouldDisplayLifetimePricingSlot: shouldDisplayLifetimePricingSlot.value,
  });

  if (!isRegionalHighlightVisible.value) {
    return cards;
  }

  return [
    {
      i18nKey: 'REGIONAL_HIGHLIGHT',
      value: REGIONAL_HIGHLIGHT_PROMOTION_VALUE,
      disabled: regionalHighlightBookingState.value.kind === 'blocked',
      showPricingSlot: true,
      displayFullDescription: false,
      accentColor: TOPLISTING_PROMOTION_CARD_ACCENT_REGIONAL_HIGHLIGHT,
    },
    ...cards.filter((card) => card.value !== ToplistingVariant.Lifetime),
  ];
});

const promotionCardsLifetimeReadonly = computed(() =>
  buildToplistingLifetimeReadonlyStepCards({
    shouldDisplayLifetimePricingSlot: shouldDisplayLifetimePricingSlot.value,
  }),
);

const v$ = useVuelidate(validationRules, {
  startDate,
  endDate,
  toplistingVariant,
});

const mapToplistingSelectionToGtmPackageLabel = (value: string): string => {
  const map: Record<string, string> = {
    [ToplistingVariant.Premium]: EventLabel.PREMIUM_TOP_LISTING,
    [ToplistingVariant.Top]: EventLabel.TOP_LISTING,
    [ToplistingVariant.Lifetime]: EventLabel.LIFETIME_TOP_LISTING,
    [ToplistingVariant.TopAbo]: EventLabel.TOP_LISTING_ABO,
    [TOPLISTING_PROMOTION_NONE]: EventLabel.NO_ADDITIONAL_VISIBILITY,
  };
  return map[value] ?? '';
};

const getSelectedVariantPriceForTracking = (): number | undefined => {
  const numericPrice =
    typeof selectedVariantPrice.value === 'number'
      ? selectedVariantPrice.value
      : Number(selectedVariantPrice.value);
  return Number.isFinite(numericPrice) ? roundToTwoDecimals(numericPrice) : undefined;
};

const getToplistingTotalCostForTracking = ({
  pricePerDay,
  durationDays,
  isFlatTotal,
}: {
  pricePerDay?: number;
  durationDays: number | null;
  isFlatTotal: boolean;
}): number | undefined => {
  if (pricePerDay === undefined) {
    return undefined;
  }
  if (isFlatTotal) {
    return pricePerDay;
  }
  return durationDays !== null ? roundToTwoDecimals(pricePerDay * durationDays) : undefined;
};

/** functions **/
const openDatePicker = (which: 'start' | 'end', event: MouseEvent): void => {
  const target = event.target as HTMLElement | null;
  if (target?.closest('input')) {
    return;
  }

  const input = (event.currentTarget as HTMLElement).querySelector<HTMLInputElement>('input');
  if (input && !input.disabled) {
    input.showPicker?.();
    trackToplistingInteraction({
      action: EventAction.LISTING_DETAILS,
      label: which === 'start' ? EventLabel.START_DATE : EventLabel.END_DATE,
      listingId: props.insertion.listing?.id ?? undefined,
      objectReferenceId: toplistingTrackingObjectReferenceId.value,
      conversionSource: props.conversionSource,
      ...ubmPriceElasticityTrackingParams.value,
    });
  }
};

const emitPromotionSelectionChangeForEmbeddedFunnel = (): void => {
  if (!props.embeddedPageLayout || !props.active) {
    return;
  }
  emit('promotion-selection-change', promotionRadioValue.value);
};

watch(promotionRadioValue, () => {
  emitPromotionSelectionChangeForEmbeddedFunnel();
});

// eslint-disable-next-line max-statements,sonarjs/cognitive-complexity
const init = (): void => {
  // In embedded mode (publication step), draft toplisting data is stored in
  // listing.valueAddedServices.rank (set by the setRankType mutation),
  // NOT in insertion.toplistingData (which is a server-side field).
  const rank = props.embeddedPageLayout
    ? props.insertion.listing?.valueAddedServices?.rank
    : undefined;
  const isLifetime = props.embeddedPageLayout
    ? props.insertion.listing?.valueAddedServices?.isLifetimeToplisting
    : false;

  if (currentBookingToplistingData.value) {
    setCurrentBookingNotificationInfo(currentBookingToplistingData.value);
  }

  if (activeToplistingData.value && hasActiveToplisting.value) {
    const data = activeToplistingData.value;
    const variant = setInitialToplistingInfo(data.listingType, data.contractModel);

    const status: ToplistingStatus = {
      beginRequest: data.beginRequest
        ? new Date(data.beginRequest).toISOString()
        : getCurrentDateInHgRetsFormat(),
      endRequest: data.endRequest ? new Date(data.endRequest).toISOString() : null,
      toplistingVariant: variant,
    };

    toplistingStatus.value = status;
    setToplistingVariant(status.toplistingVariant);
    setInitialFormValues(status);

    if (startDate.value <= getCurrentDateInHgRetsFormat()) {
      isStartDateDisabled.value = true;
    }
  } else if (rank?.type || isLifetime) {
    // Draft has saved rank data — reconstruct variant and dates
    const variant = isLifetime
      ? ToplistingVariant.Lifetime
      : rank!.type === RankType.Premium
        ? ToplistingVariant.Premium
        : ToplistingVariant.Top;

    startDate.value = rank?.start
      ? mapUtcToHgRetsDateFormat(new Date(rank.start).toISOString())
      : getCurrentDateInHgRetsFormat();
    endDate.value = rank?.end ? mapUtcToHgRetsDateFormat(new Date(rank.end).toISOString()) : null;
    // Apply 10-day default if draft had no end date
    setToplistingVariant(variant, !rank?.end);

    if (startDate.value <= getCurrentDateInHgRetsFormat()) {
      isStartDateDisabled.value = true;
    }
  } else if (props.embeddedPageLayout) {
    // Embedded funnel with no saved rank data → default to "no additional visibility"
    promotionRadioValue.value = TOPLISTING_PROMOTION_NONE;
    deselectToplisting();
    emitPromotionSelectionChangeForEmbeddedFunnel();
    return;
  } else {
    // For new bookings, set 10-day default to show summary immediately
    isStartDateDisabled.value = false;
    if (shouldPreselectTopAboInBoostVisibilityModal.value) {
      applyTopAboPreselectionIfEligible();
    } else {
      setToplistingVariant(toplistingVariant.value, true);
    }
  }

  promotionRadioValue.value = toplistingVariant.value;
  emitPromotionSelectionChangeForEmbeddedFunnel();
};

const resetData = (): void => {
  toplistingVariant.value = ToplistingVariant.Top;
  promotionRadioValue.value = ToplistingVariant.Top;
  startDate.value = getCurrentDateInHgRetsFormat();
  minStartDate.value = getCurrentDateInHgRetsFormat();
  // Set default 10-day duration for immediate summary display
  const defaultEndDate = new Date(startDate.value);
  defaultEndDate.setDate(defaultEndDate.getDate() + 9);
  endDate.value = defaultEndDate.toISOString().split('T')[0];
  isStartDateDisabled.value = false;
  toplistingCreditUsage.value = null;
  showCreditErrorMsg.value = false;
  creditDataCallCompleted.value = false;
  formattedPeriodEndDate.value = '';
  currentBookingFormattedPeriodStartDate.value = '';
  currentBookingFormattedPeriodStartDateFull.value = '';
  currentBookingFormattedPeriodEndDate.value = '';
  currentBookingLengthInDays.value = null;
  initialFormValues.value = null;
  currentBookingListingTypeTitle.value = '';
  v$.value.$reset();
};

const getCurrentFormValues = (): ToplistingStatus => ({
  beginRequest: startDate.value,
  endRequest: endDate.value,
  toplistingVariant: toplistingVariant.value,
});

const mapToplistingVariantToRankType = (variant: ToplistingVariant): RankType =>
  variant === ToplistingVariant.Premium ? RankType.Premium : RankType.Top;

const patchRankType = (): void => {
  store.commit('setRankType', {
    rankType: mapToplistingVariantToRankType(toplistingVariant.value),
    start: new Date(startDate.value),
    end: endDate.value !== null ? new Date(endDate.value) : undefined,
  });
};

const deselectToplisting = (): void => {
  store.commit('setIsLifetimeTopListing', undefined);
  store.commit('setRankType', undefined);
};

const onChangedDate = (): void => {
  v$.value.$touch();
  patchRankType();
  fetchAndProcessToplistingCreditData();
};

const onClickToplistingOption = (variant: ToplistingVariant): void => {
  v$.value.$touch();
  setToplistingVariant(variant, true);

  if (variant === ToplistingVariant.Lifetime) {
    store.commit('setRankType', undefined);
    store.commit('setIsLifetimeTopListing', true);
  } else if (variant === ToplistingVariant.TopAbo) {
    store.commit('setIsLifetimeTopListing', undefined);
    deselectToplisting();
  } else {
    store.commit('setIsLifetimeTopListing', undefined);
    patchRankType();
  }

  // Toplisting Abo and Immo Credits do not coexist, so we save an API call
  if (variant !== ToplistingVariant.TopAbo) {
    fetchAndProcessToplistingCreditData();
  }
};

const onPromotionRadioSelect = (value: string): void => {
  promotionRadioValue.value = value;

  if (value === REGIONAL_HIGHLIGHT_PROMOTION_VALUE) {
    toplistingCreditUsage.value = null;
    lifetimeToplistingCreditUsage.value = null;
    creditDataCallCompleted.value = true;
    deselectToplisting();
    return;
  }

  trackToplistingInteraction({
    action: EventAction.SELECT_TOPLISTING_PACKAGE,
    label: mapToplistingSelectionToGtmPackageLabel(value),
    listingId: props.insertion.listing?.id ?? undefined,
    objectReferenceId: toplistingTrackingObjectReferenceId.value,
    conversionSource: props.conversionSource,
    ...ubmPriceElasticityTrackingParams.value,
  });

  if (value === TOPLISTING_PROMOTION_NONE) {
    toplistingCreditUsage.value = null;
    lifetimeToplistingCreditUsage.value = null;
    creditDataCallCompleted.value = true;
    deselectToplisting();
    return;
  }
  onClickToplistingOption(value as ToplistingVariant);
};

onMounted(() => {
  v$.value.$touch();
});

const setInitialToplistingInfo = (
  listingType: RankType,
  contractModel: ContractModel,
): ToplistingVariant => {
  if (listingType === RankType.Premium) {
    currentBookingListingTypeTitle.value = t('toplisting.badge.premium');
    return ToplistingVariant.Premium;
  } else if (listingType === RankType.Top && contractModel === ContractModel.ABO) {
    currentBookingListingTypeTitle.value = t('toplisting.badge.abo');
    return ToplistingVariant.TopAbo;
  } else if (listingType === RankType.Top && contractModel === ContractModel.LIFETIME) {
    currentBookingListingTypeTitle.value = t('toplisting.badge.lifetime');
    return ToplistingVariant.Lifetime;
  } else {
    currentBookingListingTypeTitle.value = t('toplisting.badge.top');
    return ToplistingVariant.Top;
  }
};

const formatDateToHgRets = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

const getOneYearFromDate = (dateString: string): string => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) {
    const fallback = new Date();
    fallback.setFullYear(fallback.getFullYear() + 1);
    return formatDateToHgRets(fallback);
  }

  date.setFullYear(date.getFullYear() + 1);
  return formatDateToHgRets(date);
};

const setInitialFormValues = (status: ToplistingStatus): void => {
  startDate.value = status.beginRequest
    ? mapUtcToHgRetsDateFormat(status.beginRequest)
    : getCurrentDateInHgRetsFormat();
  endDate.value = status.endRequest ? mapUtcToHgRetsDateFormat(status.endRequest) : null;
  if (status.toplistingVariant === ToplistingVariant.Lifetime && !endDate.value) {
    endDate.value = getOneYearFromDate(startDate.value);
  }
  formattedPeriodEndDate.value = endDate.value ? mapToDotFormattedDate(endDate.value) : '';
  initialFormValues.value = getCurrentFormValues();

  v$.value.startDate.$touch();
};

const applyDefaultTenDayPremiumOrTopWindow = (
  toplistingVariantToSet: ToplistingVariant,
  isUserAction: boolean,
): void => {
  if (
    !isUserAction ||
    (toplistingVariantToSet !== ToplistingVariant.Top &&
      toplistingVariantToSet !== ToplistingVariant.Premium) ||
    endDate.value
  ) {
    return;
  }
  const start = new Date(startDate.value);
  const end = new Date(start);
  end.setDate(end.getDate() + 9); // +9 because start date is day 1
  endDate.value = end.toISOString().split('T')[0];
};

const setToplistingVariant = (
  toplistingVariantToSet: ToplistingVariant,
  isUserAction = false,
): void => {
  if (
    toplistingStatus.value &&
    hasActiveToplisting.value &&
    mapUtcToHgRetsDateFormat(toplistingStatus.value.beginRequest) < getCurrentDateInHgRetsFormat()
  ) {
    if (toplistingStatus.value?.toplistingVariant === toplistingVariantToSet) {
      startDate.value = mapUtcToHgRetsDateFormat(toplistingStatus.value.beginRequest);
    } else {
      startDate.value = getCurrentDateInHgRetsFormat();
      isStartDateDisabled.value = true;
    }
  }

  if (toplistingVariantToSet === ToplistingVariant.TopAbo) {
    startDate.value = getCurrentDateInHgRetsFormat();
    endDate.value = null;
  }

  if (toplistingVariantToSet === ToplistingVariant.Lifetime) {
    startDate.value = getCurrentDateInHgRetsFormat();
    endDate.value = getOneYearFromDate(startDate.value);
  }

  if (
    isUserAction &&
    toplistingVariant.value === ToplistingVariant.Lifetime &&
    (toplistingVariantToSet === ToplistingVariant.Top ||
      toplistingVariantToSet === ToplistingVariant.Premium)
  ) {
    endDate.value = null;
  }

  applyDefaultTenDayPremiumOrTopWindow(toplistingVariantToSet, isUserAction);

  toplistingVariant.value = toplistingVariantToSet;
  v$.value.toplistingVariant.$touch();
};

const revertTopAboSelectionIfUnavailable = (): void => {
  if (isEditingExistingAboBooking.value) {
    return;
  }
  if (isTopAboRadioButtonEnabled.value) {
    return;
  }
  if (toplistingVariant.value !== ToplistingVariant.TopAbo) {
    return;
  }

  topAboPreselectApplied.value = false;
  setToplistingVariant(ToplistingVariant.Top, true);
  promotionRadioValue.value = ToplistingVariant.Top;
};

const applyTopAboPreselectionIfEligible = (): void => {
  if (topAboPreselectApplied.value) {
    return;
  }
  if (!shouldPreselectTopAboInBoostVisibilityModal.value) {
    return;
  }
  if (
    toplistingVariant.value !== ToplistingVariant.Top ||
    promotionRadioValue.value !== ToplistingVariant.Top
  ) {
    return;
  }

  setToplistingVariant(ToplistingVariant.TopAbo, true);
  promotionRadioValue.value = ToplistingVariant.TopAbo;
  topAboPreselectApplied.value = true;
};

const emitToplistingBookTracking = (): void => {
  const packageSelection = promotionRadioValue.value;
  const packageLabel = mapToplistingSelectionToGtmPackageLabel(packageSelection);
  const durationDays = computeBookingLengthInDaysFromFormDates();
  const noPaidPromotionForBookTlAnalytics =
    packageSelection === TOPLISTING_PROMOTION_NONE ||
    packageLabel === EventLabel.NO_ADDITIONAL_VISIBILITY;
  const isTopAboPackage = packageSelection === ToplistingVariant.TopAbo;
  const shouldOmitPaidPackageMetrics = noPaidPromotionForBookTlAnalytics || isTopAboPackage;
  const pricePerDay = shouldOmitPaidPackageMetrics
    ? undefined
    : getSelectedVariantPriceForTracking();
  const totalCost = getToplistingTotalCostForTracking({
    pricePerDay,
    durationDays,
    isFlatTotal: toplistingVariant.value === ToplistingVariant.Lifetime || isTopAboPackage,
  });
  trackToplistingBooking({
    listingId: props.insertion.listing?.id ?? undefined,
    objectReferenceId: toplistingTrackingObjectReferenceId.value,
    conversionSource: props.conversionSource,
    packageLabel,
    durationInDays: shouldOmitPaidPackageMetrics ? undefined : durationDays,
    pricePerDay,
    totalCost,
    ...ubmPriceElasticityTrackingParams.value,
  });
};

/** Used by embedded insertion promotion step so publish emits `book_TL` for any card, including no promotion. */
const trackBookToplistingForAnalytics = (): void => {
  if (isRegionalHighlightSelected.value) {
    return;
  }
  emitToplistingBookTracking();
};

const assignRegionalHighlight = async (throwOnError: boolean): Promise<boolean> => {
  const state = regionalHighlightBookingState.value;
  const listingId = regionalHighlightListingId.value;
  if (state.kind !== 'assignable' || !listingId) {
    return false;
  }

  await store.dispatch('assignRegionalHighlightSlot', {
    slotId: state.slot.slotId,
    assignment: {
      listingId,
      ...(state.slot.listingMatch?.action === RegionalHighlightListingMatchAction.REPLACE &&
      state.slot.activeBooking?.bookingId
        ? { replaceBookingId: state.slot.activeBooking.bookingId }
        : {}),
    },
  });

  const error = store.getters.getRegionalHighlightErrors?.assign;
  if (error) {
    if (throwOnError) {
      throw error;
    }
    return false;
  }
  return true;
};

const submitRegionalHighlight = async (): Promise<void> => {
  if (!(await assignRegionalHighlight(true))) {
    throw new Error('Regional Highlight is no longer assignable');
  }
};

const deactivateRegionalHighlight = async (): Promise<void> => {
  const state = regionalHighlightBookingState.value;
  if (state.kind !== 'active') {
    throw new Error('Regional Highlight is no longer active');
  }

  await store.dispatch('deactivateRegionalHighlightSlot', {
    slotId: state.slot.slotId,
    deactivation: {
      bookingId: state.slot.activeBooking?.bookingId,
      listingId: regionalHighlightListingId.value,
    },
  });

  const error = store.getters.getRegionalHighlightErrors?.deactivate;
  if (error) {
    throw error;
  }
};

const deactivateExistingToplisting = async (): Promise<boolean> => {
  const succeeded = await store.dispatch('deleteToplisting', {
    insertionId: props.insertion?.id,
    objectReferenceId: props.insertion?.objectReference,
  });
  return succeeded === true;
};

const handleAlternativePromotionSubmission = async (): Promise<boolean> => {
  if (promotionRadioValue.value === TOPLISTING_PROMOTION_NONE) {
    emit('close-request');
    return true;
  }

  if (isRegionalHighlightSelected.value) {
    if (hasActiveToplisting.value && !(await deactivateExistingToplisting())) {
      return true;
    }
    if (await assignRegionalHighlight(false)) {
      emit('close-request');
    }
    return true;
  }

  return false;
};

const submitForm = async (): Promise<void> => {
  if (await handleAlternativePromotionSubmission()) {
    return;
  }

  v$.value.$touch();

  if (v$.value.$invalid) {
    return;
  }

  if (hasActiveRegionalHighlight.value) {
    await deactivateRegionalHighlight();
  }

  const contractModel =
    toplistingVariant.value === ToplistingVariant.TopAbo
      ? ContractModel.ABO
      : toplistingVariant.value === ToplistingVariant.Lifetime
        ? ContractModel.LIFETIME
        : ContractModel.DAILY;
  const rankType =
    toplistingVariant.value === ToplistingVariant.Premium ? RankType.Premium : RankType.Top;

  emitToplistingBookTracking();

  if (toplistingVariant.value === ToplistingVariant.TopAbo) {
    await store.dispatch('bookToplistingAbo', {
      insertionId: props.insertion?.id,
      objectReferenceId: props.insertion?.objectReference,
    });
  } else {
    const requestData: BookToplistingRequest = {
      rankType,
      beginRequest:
        hasActiveToplisting.value &&
        toplistingStatus.value?.toplistingVariant === toplistingVariant.value &&
        mapUtcToHgRetsDateFormat(toplistingStatus.value?.beginRequest) <
          getCurrentDateInHgRetsFormat()
          ? toplistingStatus.value?.beginRequest
          : (getDateForPayload(startDate.value, 3, 0) as string),
      endRequest: getDateForPayload(endDate.value, 23, 59),
      contractModel,
      sourceSystem: 'BUSINESS_MANAGER',
    };

    await store.dispatch('bookToplisting', {
      objectReferenceId: props.insertion?.objectReference,
      insertionId: props.insertion?.id,
      toplisting: requestData,
      action: hasActiveToplisting.value ? ToplistingModalAction.EDIT : ToplistingModalAction.CREATE,
    });
  }
  emit('close-request');
};

const getDateForPayload = (dateString: string | null, hours: number, minutes: number) => {
  if (dateString) {
    const date = new Date(dateString);
    date.setHours(hours, minutes);
    return date.toISOString();
  }
  return null;
};

const cancel = (): void => {
  emit('close-request');
  trackToplistingInteraction({
    action: EventAction.CANCEL_TOPLISTING,
    label: EventLabel.IN_MODAL,
    listingId: props.insertion.listing?.id ?? undefined,
    objectReferenceId: toplistingTrackingObjectReferenceId.value,
    conversionSource: props.conversionSource,
    ...ubmPriceElasticityTrackingParams.value,
  });
};

const fetchAndProcessToplistingCreditData = async (): Promise<void> => {
  if (!isImmoCreditsCustomer.value) {
    return;
  }

  creditDataCallCompleted.value = false;
  toplistingCreditUsage.value = null;
  lifetimeToplistingCreditUsage.value = null;
  showCreditErrorMsg.value = false;

  if (toplistingVariant.value === ToplistingVariant.TopAbo) {
    creditDataCallCompleted.value = true;
    return;
  }

  try {
    if (toplistingVariant.value === ToplistingVariant.Lifetime) {
      lifetimeToplistingCreditUsage.value = await fetchLifetimeToplistingCreditUsage();
      creditDataCallCompleted.value = true;
      return;
    }

    const creditStartDate =
      hasActiveToplisting.value &&
      toplistingStatus.value?.beginRequest &&
      mapUtcToHgRetsDateFormat(toplistingStatus.value.beginRequest) < getCurrentDateInHgRetsFormat()
        ? mapUtcToHgRetsDateFormat(toplistingStatus.value.beginRequest)
        : startDate.value;

    toplistingCreditUsage.value = await fetchToplistingCreditUsage(
      insertionOfferType.value!,
      toplistingVariant.value === ToplistingVariant.Premium ? RankType.Premium : RankType.Top,
      creditStartDate,
      endDate.value,
    );

    creditDataCallCompleted.value = true;
  } catch (error: any) {
    if (
      !shouldHideImmoCreditsRichUi.value &&
      toplistingVariant.value !== ToplistingVariant.Lifetime
    ) {
      showCreditErrorMsg.value = error?.statusCode >= 500;
    }
    creditDataCallCompleted.value = true;
  }
};

const computeBookingLengthInDays = (
  periodStartDate: string,
  periodEndDate: string | null,
): number | null => {
  if (!periodEndDate) {
    return null;
  }
  const daysBetween = calculateDaysBetweenTwoDates(periodStartDate, periodEndDate);
  return daysBetween !== null ? daysBetween + 1 : null;
};

/** Inclusive day count for the selected start/end. */
const computeBookingLengthInDaysFromFormDates = (): number | null =>
  computeBookingLengthInDays(startDate.value, endDate.value);

const setCurrentBookingNotificationInfo = (data: ToplistingData): void => {
  setInitialToplistingInfo(data.listingType, data.contractModel);
  const isLifetimeBooking = data.contractModel === ContractModel.LIFETIME;
  const currentBookingStartDate =
    mapToplistingRequestDateToHgRets(data.beginRequest) ?? getCurrentDateInHgRetsFormat();
  const currentBookingEndDate =
    mapToplistingRequestDateToHgRets(data.endRequest) ??
    (isLifetimeBooking ? getOneYearFromDate(currentBookingStartDate) : null);

  currentBookingFormattedPeriodStartDate.value = convertDateToDDMM(currentBookingStartDate);
  currentBookingFormattedPeriodStartDateFull.value = mapToDotFormattedDate(currentBookingStartDate);
  currentBookingFormattedPeriodEndDate.value = currentBookingEndDate
    ? mapToDotFormattedDate(currentBookingEndDate)
    : '';
  currentBookingLengthInDays.value = computeBookingLengthInDays(
    currentBookingStartDate,
    currentBookingEndDate,
  );
};

const applyRegionalHighlightPreselection = (): void => {
  if (
    ['assignable', 'blocked', 'active'].includes(regionalHighlightBookingState.value.kind) &&
    !hasActiveToplisting.value &&
    !hasSavedDraftToplisting.value
  ) {
    promotionRadioValue.value = REGIONAL_HIGHLIGHT_PROMOTION_VALUE;
    deselectToplisting();
  }
};

const fetchRegionalHighlightSlotsForListing = async (): Promise<void> => {
  if (
    !props.active ||
    !regionalHighlightEnabledForListing.value ||
    !regionalHighlightListingId.value
  ) {
    return;
  }
  await store.dispatch('fetchRegionalHighlightSlots', regionalHighlightListingId.value);
};

onMounted(async () => {
  init();
  if (!regionalHighlightEnabledForListing.value) {
    await fetchAndProcessToplistingCreditData();
    return;
  }
  await fetchRegionalHighlightSlotsForListing();
  applyRegionalHighlightPreselection();
  if (!isRegionalHighlightSelected.value) {
    await fetchAndProcessToplistingCreditData();
  }
});

/** watchers **/
watch(toplistingModalTitle, (title) => emit('update:title', title), {
  immediate: true,
});
watch(footerBindings, (bindings) => emit('update:footerBindings', bindings), {
  immediate: true,
  deep: true,
});
watch(topListingAbos, () => {
  revertTopAboSelectionIfUnavailable();
  applyTopAboPreselectionIfEligible();
});
watch(regionalHighlightBookingState, applyRegionalHighlightPreselection);
watch(
  [() => props.active, regionalHighlightEnabledForListing, regionalHighlightListingId],
  fetchRegionalHighlightSlotsForListing,
);

defineExpose({
  submitForm,
  cancel,
  toplistingTypeLabel,
  toplistingPrice,
  selectedVariantPrice,
  trackBookToplistingForAnalytics,
  submitRegionalHighlight,
  deactivateRegionalHighlight,
});
</script>

<style lang="scss" module>
@import '@/assets/variables';
@import '@/assets/mixins';

.root {
  width: 100%;
}

.embeddedRoot {
  display: contents;
}

.embedded {
  max-width: 606px;
}

.inputFields {
  padding: $space-150 $space-150 $space-100 $space-150;
  background-color: $color-white;
  border: 1px solid $color-deep-blue-20;
  border-radius: $border-radius-l;
}

.inlineFooter {
  display: flex;
  flex-wrap: wrap;
  gap: $space-100;
  align-items: center;
  justify-content: space-between;
  margin-top: $space-150;
}

.inlineFooterButtons {
  display: flex;
  flex-wrap: wrap;
  gap: $space-100;
  margin-left: auto;
}

.icon {
  width: $icon-size-md;
  height: $icon-size-md;
}

.wrapperIcon {
  @include icon-link-wrapper;
}

.toplistingForm {
  padding-right: $space-50;

  @media screen and (max-width: $viewport-sm-min) {
    display: flex;
    flex-direction: column;
    min-height: calc(100% - #{$space-400});

    .innerWrapper {
      flex-grow: 1;
    }
  }
}

/* Funnel / listing promotion: normal block flow — avoid flex + min-height that break full-width pages. */
.toplistingFormPage {
  display: block;
  width: 100%;
  min-height: 0;
  padding: 0;
  margin: 0;
}

.notificationMessageContainer {
  width: 100%;
  margin: 0 12px;

  :global(.NotificationMessage_notification_27f73 .NotificationMessage_messageWrapper_c428f) {
    gap: 0;
    margin-bottom: 0;
  }
}

.notificationMessageLifetime {
  margin-bottom: $space-100;
}

.currentBookingWithoutTitle {
  margin-top: $space-100;
}

.currentBookingMsgContainer {
  display: flex;
  flex: 1;
  flex-wrap: nowrap;
  gap: 0.25rem;
  padding: 0.3rem 0;
}

.currentBookingIconContainer {
  margin-right: 0.75rem;
}

.currentBookingMsgTitle {
  @include body-3-bold;

  & {
    align-self: center;
  }
}

.currentBookingMsgBody {
  @include body-3;
}

.disclaimer,
.disclaimerLifetime {
  @include body-3(#43607a);

  & {
    margin: $space-100 0 $space-200;
  }
}

.disclaimerLifetimeDate {
  @include body-3-bold(#43607a);
}

.durationHint {
  @include body-3($color-grey-90);

  & {
    padding: $space-50 0;
  }
}

.durationHintTooltipBody {
  margin: 0;
}

.regionalHighlightWarning {
  margin-top: $space-150;
}

.skeletonParagraph {
  height: $space-100;
  margin-bottom: $space-25;
}

.skeletonCollapsible {
  width: 100%;
  height: $space-600;
  margin-bottom: $space-75;
}

.skeletonHeader {
  width: 20%;
  height: $space-100 + $space-75;
  margin-top: $space-100;
  margin-bottom: $space-100;
}

.priceContainer {
  display: flex;
  justify-content: space-between;
}

.priceWrapper {
  @include body-3;

  .price {
    @include body-3-bold;
  }
}

.startDateWrapper {
  margin-bottom: 0;
}

.endDateField {
  --toplisting-end-date-tooltip-left: 0;

  :global([class*='HgFormFieldWrapper_tooltip']) {
    right: auto;
    left: var(--toplisting-end-date-tooltip-left);
  }
}

:global(html[lang='en']) .endDateField {
  --toplisting-end-date-tooltip-left: 3.75rem;
}

:global(html[lang='de-CH']) .endDateField {
  --toplisting-end-date-tooltip-left: 4.5rem;
}

:global(html[lang='fr-CH']) .endDateField {
  --toplisting-end-date-tooltip-left: 4.75rem;
}

:global(html[lang='it-CH']) .endDateField {
  --toplisting-end-date-tooltip-left: 5rem;
}

.noPromotionBeforePromotionCards {
  margin-bottom: $space-150;
}

.regionalHighlightLoading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20rem;
}

.immoCreditsSkeletonBlock {
  width: 100%;
}

.immoCreditsSimpleNotice {
  width: 100%;
  margin-top: $space-100;
}

.immoCreditsSimpleNoticeLink {
  display: inline;
  margin-left: $space-25;
}

.loadingIndicator {
  width: var(--typo-body-3-font-size);
  padding-right: $space-100;
  filter: grayscale(1) brightness(300%);
}

@media screen and (max-width: $viewport-sm-min) {
  .startDateWrapper {
    margin-bottom: $form-field-margin-bottom;
  }
}
</style>
